import {ColumnSpec} from '@xh/hoist/cmp/grid';
import {OrderStatusEnum, OrderTimingEnum, OrderTypeEnum, TransactionTypeEnum} from '../../../../data/DataTypes';
import {div, hbox, span, strong} from '@xh/hoist/cmp/layout';
import './WorkingOrders.scss';
import {fmtDateTimeSec, numberRenderer, priceRenderer} from '@xh/hoist/format';

/** Label and style key for each order status. The key picks the dot color - see WorkingOrders.scss. */
const STATUS: Record<OrderStatusEnum, {label: string; key: string}> = {
    [OrderStatusEnum.Open]: {label: 'Open', key: 'open'},
    [OrderStatusEnum.OpenContingent]: {label: 'Contingent', key: 'contingent'},
    [OrderStatusEnum.OpenPartialFill]: {label: 'Working', key: 'working'},
    [OrderStatusEnum.CancelPending]: {label: 'Cxl Pending', key: 'cancel-pending'},
    [OrderStatusEnum.ClosedPartialFill]: {label: 'Partial', key: 'partial'},
    [OrderStatusEnum.Filled]: {label: 'Filled', key: 'filled'},
    [OrderStatusEnum.Cancelled]: {label: 'Cancelled', key: 'cancelled'}
};

const SIDE: Record<TransactionTypeEnum, {label: string; key: string}> = {
    [TransactionTypeEnum.Buy]: {label: 'Buy', key: 'buy'},
    [TransactionTypeEnum.Sell]: {label: 'Sell', key: 'sell'},
    [TransactionTypeEnum.SellShort]: {label: 'Short', key: 'short'},
    [TransactionTypeEnum.BuyToCover]: {label: 'Cover', key: 'cover'}
};

const ORDER_TYPE: Record<OrderTypeEnum, string> = {
    [OrderTypeEnum.Market]: 'MKT',
    [OrderTypeEnum.Limit]: 'LMT',
    [OrderTypeEnum.Stop]: 'STP',
    [OrderTypeEnum.StopLimit]: 'STP LMT'
};

const ORDER_TIMING: Record<OrderTimingEnum, string> = {
    [OrderTimingEnum.Day]: 'DAY',
    [OrderTimingEnum.DayPlus]: 'DAY+',
    [OrderTimingEnum.GTC]: 'GTC',
    [OrderTimingEnum.EXT]: 'EXT',
    [OrderTimingEnum.FOK]: 'FOK'
};

const ORDER_TYPE_NAMES: Record<OrderTypeEnum, string> = {
    [OrderTypeEnum.Market]: 'Market',
    [OrderTypeEnum.Limit]: 'Limit',
    [OrderTypeEnum.Stop]: 'Stop',
    [OrderTypeEnum.StopLimit]: 'Stop Limit'
};

/** Status as a colored dot and a short label. The dot pulses while the order is working. */
export const orderStatusCol: ColumnSpec = {
    field: 'orderStatus',
    headerName: 'Status',
    width: 96,
    renderer: (v: OrderStatusEnum) => {
        const status = STATUS[v];
        return status ? span({className: `order-status order-status--${status.key}`, item: status.label}) : v;
    }
};

export const orderIdCol: ColumnSpec = {field: 'id', headerName: 'ID', width: 90, cellClass: 'order-muted'};

export const orderTickerCol: ColumnSpec = {field: 'ticker', width: 70, renderer: v => strong(v)};

/** Side as a flat badge - a solid fill under the terminal style, a tint under standard. */
export const transactionTypeCol: ColumnSpec = {
    field: 'transactionType',
    headerName: 'Action',
    width: 66,
    align: 'center',
    renderer: (v: TransactionTypeEnum) => {
        const side = SIDE[v];
        return side ? div({className: `order-side order-side--${side.key}`, item: side.label}) : v;
    }
};

export const orderTimingCol: ColumnSpec = {
    field: 'orderTiming',
    headerName: 'TIF',
    headerTooltip: 'Time in force',
    width: 52,
    cellClass: 'order-muted',
    renderer: (v: OrderTimingEnum) => ORDER_TIMING[v] ?? v
};

export const orderTypeCol: ColumnSpec = {
    field: 'orderType',
    headerName: 'Type',
    width: 64,
    cellClass: 'order-muted',
    renderer: (v: OrderTypeEnum) => ORDER_TYPE[v] ?? v,
    tooltip: (v: OrderTypeEnum) => ORDER_TYPE_NAMES[v] ?? v
};

/** Filled share as a thin bar and a percent, sized to fit any row height. Striped while the order is working. */
export const progressCol: ColumnSpec = {
    field: 'progress',
    width: 110,
    rendererIsComplex: true,
    renderer: (v, {record}) => {
        const {orderStatus, filledQuantity, quantity} = record.data,
            pct = quantity ? Math.min(Math.max(filledQuantity / quantity, 0), 1) : 0,
            state =
                orderStatus === OrderStatusEnum.OpenPartialFill || orderStatus === OrderStatusEnum.CancelPending
                    ? 'active'
                    : orderStatus === OrderStatusEnum.Filled
                      ? 'done'
                      : orderStatus === OrderStatusEnum.Cancelled || orderStatus === OrderStatusEnum.ClosedPartialFill
                        ? 'closed'
                        : 'idle';

        return hbox({
            className: `order-progress order-progress--${state}`,
            items: [
                div({className: 'order-progress__track', item: div({className: 'order-progress__fill', style: {width: `${pct * 100}%`}})}),
                span({className: 'order-progress__pct', item: `${Math.round(pct * 100)}%`})
            ]
        });
    }
};

const qtyRenderer = numberRenderer({precision: 0}),
    pxRenderer = priceRenderer({precision: 2, prefix: '$'});

export const limitPriceCol: ColumnSpec = {field: 'limitPrice', headerName: 'Limit', align: 'right', width: 84, renderer: pxRenderer};

export const quantityCol: ColumnSpec = {field: 'quantity', headerName: 'Qty', align: 'right', width: 70, renderer: qtyRenderer};

export const filledQuantityCol: ColumnSpec = {field: 'filledQuantity', headerName: 'Filled', align: 'right', width: 70, renderer: qtyRenderer};

export const executionPriceCol: ColumnSpec = {field: 'executionPrice', headerName: 'Avg Px', align: 'right', width: 84, renderer: pxRenderer};

export const bidCol: ColumnSpec = {field: 'bid', align: 'right', width: 84, renderer: pxRenderer, cellClass: 'order-bid'};

export const askCol: ColumnSpec = {field: 'ask', align: 'right', width: 84, renderer: pxRenderer, cellClass: 'order-ask'};

export const markCol: ColumnSpec = {field: 'mark', align: 'right', width: 84, renderer: pxRenderer};

export const estAmountCol: ColumnSpec = {field: 'estAmount', headerName: 'Est. Amount', align: 'right', width: 110, renderer: pxRenderer};

export const estCommissionCol: ColumnSpec = {field: 'estCommission', headerName: 'Est. Comm.', align: 'right', width: 84, renderer: pxRenderer};

export const estFeesCol: ColumnSpec = {field: 'estFees', headerName: 'Est. Fees', align: 'right', width: 84, renderer: pxRenderer};

export const updatedCol: ColumnSpec = {
    field: 'updated',
    headerName: 'Updated',
    width: 160,
    cellClass: 'order-muted',
    renderer: v => fmtDateTimeSec(v)
};
